import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CommonTable from "../../common-components/common-tables/CommonTable";
import { fetchDefaultTrainersData } from "../../../redux/reducers/apiSlice";
import {
  CallIcon,
  EmailIcon,
  PersonIcon,
  ReloadIcon,
  XCircleIcon,
} from "../../../images/icons/icons";
import useTableSorting from "../../../hooks/useTableSorting";
import SortableHeader from "../../common-components/SortableHeader";

const TMETrainerwiseAnalysis = ({ userInfoData, allFilters, setModValues }) => {
  const dispatch = useDispatch();
  const { defaultTrainers, loadingDTM, error } = useSelector(
    (state) => state.api
  );

  const iniDatas = {
    pagination: [],
    tbData: [],
  };
  const [allDatas, setAllDatas] = useState(iniDatas);
  const [isPage, setIsPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(10);

  const { handleSort, getSortDirection, isFieldSorted, sortedData } =
    useTableSorting(allDatas.tbData || []);
  const iniFilters = {
    limit: pageLimit,
    page: isPage + 1,
    gym_id: allFilters.selectedGym?.value || "",
  };
  const [filters, setFilters] = useState(iniFilters);

  function getTrainerwiseData(filters) {
    dispatch(
      fetchDefaultTrainersData({
        filters: filters,
        token: userInfoData?.token,
      })
    );
  }

  useEffect(() => {
    getTrainerwiseData(filters);
  }, [filters]);

  useEffect(() => {
    setFilters((prevs) => ({
      ...prevs,
      limit: pageLimit,
      page: isPage + 1,
      gym_id: allFilters.selectedGym?.value || "",
    }));
  }, [isPage, pageLimit, allFilters.selectedGym]);

  useEffect(() => {
    if (
      !loadingDTM &&
      !error &&
      defaultTrainers?.status &&
      defaultTrainers?.data
    ) {
      setAllDatas((prevs) => ({
        ...prevs,
        tbData: defaultTrainers.data,
        pagination: defaultTrainers?.pagination || [],
      }));
    } else {
      setAllDatas(iniDatas);
    }
  }, [loadingDTM, error, defaultTrainers]);

  const tbColumns = [
    {
      dataField: "gym_name",
      text: "Gym Name",
      formatter: (_, row) => {
        const gymName = row?.gym_name || "NA";
        const isLong = gymName.length > 30;

        return (
          <span
            className={`tb-data tb-link ${isLong ? "tb-data-long" : ""}`}
            onClick={() =>
              setModValues((prevs) => ({
                ...prevs,
                isModOpen: true,
                modName: "tme-analysis-details",
                rowData: row,
              }))
            }
          >
            {gymName}
          </span>
        );
      },
    },
    {
      dataField: "trainer_id",
      text: "trainer info",
      formatter: (_, row) => {
        const email = row?.trainer_email || null;
        const isEmailLong = email?.length > 25;

        return (
          <>
            {row?.trainer_id ? (
              <div className="tb-flex-col">
                <div className="tb-flex">
                  <span className="tb-data tb-info-icon">
                    <PersonIcon />
                  </span>{" "}
                  <span className="tb-data">{row?.trainer_name || "NA"}</span>{" "}
                </div>

                <div className="tb-flex">
                  <span className="tb-data tb-info-icon">
                    <CallIcon />
                  </span>{" "}
                  <span className="tb-data">{row?.trainer_mobile || "NA"}</span>{" "}
                </div>

                <div className="tb-flex">
                  <span className="tb-data tb-info-icon">
                    <EmailIcon />
                  </span>{" "}
                  <span
                    className={`tb-data lowercase ${
                      isEmailLong ? "tb-data-long" : ""
                    }`}
                  >
                    {row?.trainer_email || "NA"}
                  </span>
                </div>
              </div>
            ) : (
              <span className="tb-data tb-icon">
                <XCircleIcon />
              </span>
            )}
          </>
        );
      },
    },
    {
      dataField: "total_active_members",
      text: (
        <SortableHeader
          text="Assigned Members"
          field="total_active_members"
          onSort={handleSort}
          sortDirection={getSortDirection("total_active_members")}
          isSorted={isFieldSorted("total_active_members")}
        />
      ),
      // wrapHeader: true,
      formatter: (_, row) => {
        return (
          <span className="tb-data tb-badge tb-badge-success">
            {row?.total_active_members || 0} active
          </span>
        );
      },
    },
    {
      dataField: "total_expired_members",
      text: (
        <SortableHeader
          text="Lost Members"
          field="total_expired_members"
          onSort={handleSort}
          sortDirection={getSortDirection("total_expired_members")}
          isSorted={isFieldSorted("total_expired_members")}
        />
      ),
      // wrapHeader: true,
      formatter: (_, row) => {
        return (
          <span className="tb-data tb-badge not-assigned">
            {row?.total_expired_members || 0} expired
          </span>
        );
      },
    },
    {
      dataField: "avg_rating",
      text: (
        <SortableHeader
          text="Trainer Rating"
          field="avg_rating"
          onSort={handleSort}
          sortDirection={getSortDirection("avg_rating")}
          isSorted={isFieldSorted("avg_rating")}
        />
      ),
      // wrapHeader: true,
      formatter: (_, row) => {
        return (
          <span className="tb-data tb-badge tb-badge-primary tb-min-wid-60px">
            {parseFloat(row?.avg_rating || 0).toFixed(1) || 0} ★
          </span>
        );
      },
    },
    {
      dataField: "renewal_members",
      text: (
        <SortableHeader
          text="PT Renewal"
          field="renewal_members"
          onSort={handleSort}
          sortDirection={getSortDirection("renewal_members")}
          isSorted={isFieldSorted("renewal_members")}
        />
      ),
      // wrapHeader: true,
      formatter: (_, row) => {
        return (
          <span className="tb-data tb-badge tb-badge-success-2">
            {row?.renewal_members || 0} members
          </span>
        );
      },
    },
  ];

  return (
    <div className="flex-col-tables-container">
      <div className="tme-tb-header">
        <h3 className="tm-tb-header-title">
          All Trainers ({defaultTrainers?.pagination?.[0]?.pagination || 0}){" "}
          <button
            className="page-heading-reload-btn"
            onClick={() => getTrainerwiseData(filters)}
            title="Reload"
            type="button"
          >
            <ReloadIcon />
          </button>
        </h3>

        <div className="common-table-container tb-bordered">
          <CommonTable
            tableData={sortedData}
            tablePagination={allDatas.pagination}
            tableColumns={tbColumns}
            isPage={isPage}
            setIsPage={setIsPage}
            pageLimit={pageLimit}
            setPageLimit={setPageLimit}
            isLoading={loadingDTM}
          />
        </div>
      </div>
    </div>
  );
};

export default TMETrainerwiseAnalysis;
